import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Shield, Mail, Users, Zap, Clock, Star, Briefcase, FileText, Bell, ChevronRight, Globe, Smartphone } from 'lucide-react';
import { partnerPages } from '../data/partnerPages';
import { keywordPages } from '../data/keywordPages';
import FooterTrust from '../components/FooterTrust';

const BOT_LINK = "https://t.me/instant_email_bot";
const STAYMII_HR_LINK = "https://staymii.com/staymii-hr";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_staymii-telegram-bot/artifacts/dbwmuy0k_A9358B2D-0233-4058-88E9-3B869B36D815.png";
const HR_IMG = "https://images.unsplash.com/photo-1631651693480-97f1132e333d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxqb2IlMjBhcHBsaWNhdGlvbiUyMGZvcm0lMjBoaXJpbmclMjBIUiUyMHJlY3J1aXRtZW50JTIwdGVhbXxlbnwwfHx8fDE3NzkzNjEyMTB8MA&ixlib=rb-4.1.0&q=85";
const TELEGRAM_IMG = "https://images.pexels.com/photos/16841808/pexels-photo-16841808.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const fadeLeft = { hidden: { opacity: 0, x: -30 }, visible: { opacity: 1, x: 0, transition: { duration: 0.5 } } };
const fadeRight = { hidden: { opacity: 0, x: 30 }, visible: { opacity: 1, x: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

function trackCTA(action) {
  if (typeof window !== 'undefined' && window.gtag) window.gtag('event', action, { event_category: 'Partner_CTA' });
}

function TelegramIcon({ className }) {
  return (<svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>);
}

export default function PartnerPage({ slug: propSlug }) {
  const location = useLocation();
  const slug = propSlug || location.pathname.replace(/^\//, '');
  const page = partnerPages.find(p => p.slug === slug);
  if (!page) return (<div className="min-h-screen bg-white flex items-center justify-center"><div className="text-center"><h1 className="font-outfit font-bold text-2xl text-gray-900 mb-4">Page Not Found</h1><Link to="/" className="text-[#0088CC] font-manrope font-semibold hover:underline">Back to Home</Link></div></div>);

  const relatedKw = keywordPages.filter(p => page.relatedSlugs.includes(p.slug));

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <SEOMeta page={page} />

      {/* ─── HEADER ─── */}
      <header className="bg-[#0f172a] sticky top-0 z-50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="partner-logo-link">
            <img src={LOGO_URL} alt="TelegramEmailBot" className="w-7 h-7 rounded-md object-cover" />
            <span className="font-outfit font-bold text-lg text-white">TelegramEmailBot</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 font-manrope text-sm text-gray-400">
            <Link to="/use-cases" className="hover:text-white transition-colors">Use Cases</Link>
            <Link to="/telegram-email-bot" className="hover:text-white transition-colors">Guides</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
          </nav>
          <div className="flex items-center gap-3">
            <a href={STAYMII_HR_LINK} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex font-manrope text-sm font-semibold text-emerald-400 border border-emerald-500/40 px-4 py-2 rounded-full hover:bg-emerald-500/10 transition-all">StayMii HR</a>
            <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" onClick={() => trackCTA('header')} className="font-manrope text-sm font-semibold bg-[#0088CC] text-white px-5 py-2 rounded-full hover:bg-[#006699] transition-all shadow-md" data-testid="partner-header-cta">Start Free</a>
          </div>
        </div>
      </header>

      {/* ─── DARK HERO ─── */}
      <section className="relative bg-[#0f172a] pt-16 pb-24 sm:pt-24 sm:pb-32 px-4 overflow-hidden" data-testid="partner-hero">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.08),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(0,136,204,0.06),transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.nav variants={fadeUp} className="flex items-center gap-2 text-sm font-manrope text-gray-500 mb-8">
                <Link to="/" className="hover:text-emerald-400 transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-gray-400">StayMii HR</span>
              </motion.nav>

              <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6">
                <span className="inline-flex items-center gap-1.5 bg-emerald-500/15 text-emerald-400 font-manrope text-xs font-bold px-3 py-1.5 rounded-full tracking-wide uppercase"><Briefcase className="w-3 h-3" /> Free HR Tool</span>
                <span className="inline-flex items-center gap-1.5 bg-[#0088CC]/15 text-sky-400 font-manrope text-xs font-bold px-3 py-1.5 rounded-full tracking-wide uppercase"><Bell className="w-3 h-3" /> Telegram Alerts</span>
              </motion.div>

              <motion.h1 variants={fadeUp} className="font-outfit font-black text-4xl sm:text-5xl lg:text-[3.5rem] text-white leading-[1.08] mb-6">
                Free Job Application Form<br /><span className="bg-gradient-to-r from-emerald-400 to-sky-400 bg-clip-text text-transparent">+ Instant Telegram Alerts</span>
              </motion.h1>

              <motion.p variants={fadeUp} className="font-manrope text-lg text-gray-400 mb-8 leading-relaxed max-w-lg">
                Share one form link everywhere. Every applicant from every job portal — centralized. Your HR team gets notified in Telegram <strong className="text-white">within seconds</strong>.
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                <a href={STAYMII_HR_LINK} target="_blank" rel="noopener noreferrer" onClick={() => trackCTA('hero_staymii')} className="inline-flex items-center gap-2.5 bg-emerald-500 text-white font-manrope font-bold px-8 py-4 rounded-full hover:bg-emerald-600 transition-all hover:-translate-y-0.5 shadow-lg shadow-emerald-500/25" data-testid="partner-hero-staymii">
                  <Briefcase className="w-5 h-5" /> Try StayMii HR Free
                </a>
                <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" onClick={() => trackCTA('hero_telegram')} className="inline-flex items-center gap-2.5 border border-white/20 text-white font-manrope font-bold px-8 py-4 rounded-full hover:bg-white/5 transition-all" data-testid="partner-hero-cta">
                  <TelegramIcon className="w-5 h-5" /> Get Telegram Bot
                </a>
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm text-gray-500 font-manrope">
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" /> 100% free forms</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" /> No credit card</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" /> 30-second signup</span>
              </motion.div>
            </motion.div>

            {/* Right side — dual image card */}
            <motion.div initial="hidden" animate="visible" variants={fadeRight} className="hidden lg:block">
              <div className="relative">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 shadow-2xl">
                  <img src={HR_IMG} alt="Job application form on desk" className="rounded-2xl w-full h-52 object-cover mb-4" loading="eager" />
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center"><FileText className="w-4 h-4 text-emerald-400" /></div>
                    <div><p className="font-outfit font-bold text-white text-sm">New Application Received</p><p className="font-manrope text-xs text-gray-500">via StayMii HR Form</p></div>
                  </div>
                  <div className="bg-[#0088CC]/10 border border-[#0088CC]/20 rounded-xl p-3 flex items-start gap-3">
                    <TelegramIcon className="w-5 h-5 text-[#0088CC] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-manrope text-xs text-[#0088CC] font-semibold">Telegram Notification</p>
                      <p className="font-manrope text-xs text-gray-400 mt-1">New applicant: <strong className="text-white">Sarah Chen</strong> applied for Front Desk Agent. Resume attached.</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl" />
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#0088CC]/10 rounded-full blur-3xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── STATS BAR ─── */}
      <section className="bg-white border-b border-gray-100 py-8 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { value: "Free", label: "Job Application Form" },
            { value: "30s", label: "Signup Time" },
            { value: "<1 min", label: "Telegram Alert Speed" },
            { value: "$14.99/yr", label: "Telegram Alerts (optional)" },
          ].map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <p className="font-outfit font-black text-2xl sm:text-3xl text-gray-900">{stat.value}</p>
              <p className="font-manrope text-xs sm:text-sm text-gray-500 mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── HOW IT WORKS — VISUAL FLOW ─── */}
      <section className="py-20 sm:py-28 px-4 bg-white" data-testid="partner-how">
        <div className="max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-emerald-500 mb-3">How It Works</motion.p>
            <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl text-gray-900 mb-4">From Job Post to Telegram Alert in 4 Steps</motion.h2>
          </motion.div>

          <div className="space-y-8 sm:space-y-0 sm:grid sm:grid-cols-4 sm:gap-6">
            {[
              { num: "1", icon: <Globe className="w-6 h-6" />, title: "Sign Up Free", desc: "Create your StayMii HR account in 30 seconds. No credit card needed.", color: "emerald" },
              { num: "2", icon: <FileText className="w-6 h-6" />, title: "Create Your Form", desc: "Build your job application form. Get a shareable link for any platform.", color: "sky" },
              { num: "3", icon: <TelegramIcon className="w-6 h-6" />, title: "Connect Telegram", desc: "Start @instant_email_bot, get your email, paste it in StayMii HR settings.", color: "blue" },
              { num: "4", icon: <Bell className="w-6 h-6" />, title: "Get Instant Alerts", desc: "Every new application triggers an instant Telegram notification to your team.", color: "violet" },
            ].map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }} className="relative">
                {i < 3 && <div className="hidden sm:block absolute top-12 left-[calc(50%+40px)] right-[calc(-50%+40px)] h-[2px] bg-gradient-to-r from-gray-200 to-gray-100 z-0" />}
                <div className="relative z-10 text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white shadow-lg ${
                    step.color === 'emerald' ? 'bg-emerald-500 shadow-emerald-500/25' :
                    step.color === 'sky' ? 'bg-sky-500 shadow-sky-500/25' :
                    step.color === 'blue' ? 'bg-[#0088CC] shadow-[#0088CC]/25' :
                    'bg-violet-500 shadow-violet-500/25'
                  }`}>
                    {step.icon}
                  </div>
                  <div className="font-outfit font-black text-xs text-gray-400 mb-2 uppercase tracking-widest">Step {step.num}</div>
                  <h3 className="font-outfit font-bold text-lg text-gray-900 mb-2">{step.title}</h3>
                  <p className="font-manrope text-sm text-gray-500 leading-relaxed max-w-[220px] mx-auto">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SPLIT SECTION — WHY STAYMII HR ─── */}
      <section className="py-20 sm:py-28 px-4 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft}>
              <img src={TELEGRAM_IMG} alt="Telegram app on phone showing notifications" className="rounded-3xl shadow-xl w-full h-[400px] object-cover" loading="lazy" />
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-emerald-500 mb-3">The Problem</motion.p>
              <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl text-gray-900 mb-6">Why HR Teams Are Switching</motion.h2>
              <div className="space-y-4">
                {[
                  { bad: "Resumes scattered across 5+ email inboxes", good: "All applications in one dashboard" },
                  { bad: "Good candidates hired by competitors first", good: "Respond within minutes via Telegram" },
                  { bad: "Different formats from different job portals", good: "One standardized form for all" },
                  { bad: "HR manager checks email twice a day", good: "Instant push notification on phone" },
                  { bad: "No team visibility on applications", good: "Whole HR team sees alerts in Telegram group" },
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeUp} className="flex items-start gap-4 bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                    <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mt-0.5"><span className="text-red-500 text-xs font-bold">x</span></div>
                    <div className="flex-1">
                      <p className="font-manrope text-sm text-gray-400 line-through">{item.bad}</p>
                      <p className="font-manrope text-sm text-gray-900 font-semibold flex items-center gap-2 mt-1"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{item.good}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── FEATURES GRID ─── */}
      <section className="py-20 sm:py-28 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-[#0088CC] mb-3">Features</motion.p>
            <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl text-gray-900 mb-4">Everything You Need to Hire Faster</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <FileText className="w-5 h-5" />, title: "Free Application Forms", desc: "Unlimited job forms with shareable links. Mobile-friendly. No cost ever.", bg: "bg-emerald-50", iconColor: "text-emerald-600" },
              { icon: <Users className="w-5 h-5" />, title: "Centralized Tracking", desc: "Every applicant from every source in one dashboard. JobStreet, LinkedIn, your website — all unified.", bg: "bg-sky-50", iconColor: "text-sky-600" },
              { icon: <Bell className="w-5 h-5" />, title: "Instant Telegram Alerts", desc: "Get push notifications in Telegram the second someone applies. Never miss a candidate.", bg: "bg-violet-50", iconColor: "text-violet-600" },
              { icon: <Smartphone className="w-5 h-5" />, title: "Mobile Responsive", desc: "Candidates apply from any device in under 2 minutes. Professional experience.", bg: "bg-amber-50", iconColor: "text-amber-600" },
              { icon: <Users className="w-5 h-5" />, title: "Team Group Alerts", desc: "Add TelegramEmailBot to a group. Your entire HR team sees every application.", bg: "bg-rose-50", iconColor: "text-rose-600" },
              { icon: <Zap className="w-5 h-5" />, title: "AI-Powered Summaries", desc: "TelegramEmailBot summarizes each application. Assess candidates at a glance.", bg: "bg-teal-50", iconColor: "text-teal-600" },
            ].map((f, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className={`w-11 h-11 ${f.bg} rounded-xl flex items-center justify-center mb-4 ${f.iconColor}`}>{f.icon}</div>
                <h3 className="font-outfit font-bold text-base text-gray-900 mb-2">{f.title}</h3>
                <p className="font-manrope text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── DETAILED CONTENT ─── */}
      <section className="py-16 sm:py-20 px-4 bg-[#F8F9FA]" data-testid="partner-content">
        <div className="max-w-3xl mx-auto">
          <div className="keyword-content font-manrope text-[17px] leading-[1.8] text-gray-700" dangerouslySetInnerHTML={{ __html: page.content }} />
        </div>
      </section>

      {/* ─── MID CTA ─── */}
      <section className="px-4 py-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#0f172a] rounded-3xl p-8 sm:p-12 overflow-hidden relative">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.15),transparent_60%)]" />
            <div className="relative sm:flex sm:items-center sm:justify-between sm:gap-8">
              <div className="mb-6 sm:mb-0">
                <h3 className="font-outfit font-bold text-2xl sm:text-3xl text-white mb-2">Start Hiring Smarter Today</h3>
                <p className="font-manrope text-gray-400">Free job application form + Instant Telegram alerts. Setup in 30 seconds.</p>
              </div>
              <div className="flex flex-col gap-3 flex-shrink-0">
                <a href={STAYMII_HR_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-emerald-500 text-white font-manrope font-bold px-6 py-3.5 rounded-full hover:bg-emerald-600 transition-all text-sm shadow-lg shadow-emerald-500/25">
                  <Briefcase className="w-4 h-4" /> StayMii HR (Free)
                </a>
                <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-manrope font-bold px-6 py-3.5 rounded-full hover:bg-white/5 transition-all text-sm">
                  <TelegramIcon className="w-4 h-4" /> Telegram Bot
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section className="py-16 sm:py-20 px-4 bg-[#0f172a]" data-testid="partner-pricing">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 font-manrope font-semibold text-xs uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
              <Star className="w-3.5 h-3.5" /> Simple Pricing
            </div>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-10">Free to Start. Affordable to Scale.</h2>
            <div className="grid sm:grid-cols-2 gap-6 text-left">
              <div className="bg-white/5 border border-emerald-500/30 rounded-2xl p-7 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">FREE</div>
                <Briefcase className="w-8 h-8 text-emerald-400 mb-4" />
                <p className="font-outfit font-bold text-xl text-white mb-1">StayMii HR</p>
                <p className="font-outfit font-black text-4xl text-white mb-4">$0<span className="text-lg text-gray-500 font-normal"> forever</span></p>
                <div className="space-y-2 mb-6">
                  {["Unlimited job forms", "Applicant tracking", "Resume uploads", "Mobile-friendly forms", "Dashboard access"].map((f, i) => (
                    <p key={i} className="flex items-center gap-2 font-manrope text-sm text-gray-400"><Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />{f}</p>
                  ))}
                </div>
                <a href={STAYMII_HR_LINK} target="_blank" rel="noopener noreferrer" className="block text-center bg-emerald-500 text-white font-manrope font-bold py-3.5 rounded-full hover:bg-emerald-600 transition-all">Sign Up Free</a>
              </div>
              <div className="bg-white/5 border border-[#0088CC]/30 rounded-2xl p-7 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-[#0088CC] text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">1 MONTH FREE</div>
                <TelegramIcon className="w-8 h-8 text-[#0088CC] mb-4" />
                <p className="font-outfit font-bold text-xl text-white mb-1">Telegram Alerts</p>
                <p className="font-outfit font-black text-4xl text-white mb-4">$14.99<span className="text-lg text-gray-500 font-normal">/year</span></p>
                <div className="space-y-2 mb-6">
                  {["Instant Telegram notifications", "AI application summaries", "Team group alerts", "Unlimited alerts", "No credit card to start"].map((f, i) => (
                    <p key={i} className="flex items-center gap-2 font-manrope text-sm text-gray-400"><Check className="w-4 h-4 text-[#0088CC] flex-shrink-0" />{f}</p>
                  ))}
                </div>
                <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="block text-center bg-[#0088CC] text-white font-manrope font-bold py-3.5 rounded-full hover:bg-[#006699] transition-all">Get Started ($4.99)</a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── RELATED ─── */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-outfit font-bold text-2xl text-gray-900 text-center mb-10">Related Guides</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {relatedKw.map((r, i) => (
              <Link key={i} to={`/${r.slug}`} className="block bg-[#F8F9FA] border border-gray-200 rounded-2xl p-6 hover:shadow-lg hover:border-[#0088CC]/30 hover:-translate-y-1 transition-all group">
                <div className="w-10 h-10 bg-[#0088CC]/10 rounded-xl flex items-center justify-center mb-4"><Mail className="w-5 h-5 text-[#0088CC]" /></div>
                <h3 className="font-outfit font-bold text-base text-gray-900 group-hover:text-[#0088CC] transition-colors mb-2">{r.h1}</h3>
                <p className="font-manrope text-sm text-gray-500 line-clamp-2 mb-3">{r.subtitle}</p>
                <span className="inline-flex items-center gap-1 font-manrope text-xs font-semibold text-[#0088CC]">Read guide <ArrowRight className="w-3 h-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-[#0A0A0A] text-gray-300 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TelegramEmailBot" className="w-5 h-5 rounded object-cover" />
              <span className="font-outfit font-bold text-white text-sm">TelegramEmailBot</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-manrope text-sm">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/use-cases" className="hover:text-white transition-colors">Use Cases</Link>
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <a href={STAYMII_HR_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">StayMii HR</a>
            </div>
            <span className="font-manrope text-xs text-gray-300">&copy; 2026 TelegramEmailBot</span>
          </div>
          <FooterTrust variant="dark" />
        </div>
      </footer>

      <FAQSchema page={page} />

      <style>{`
        .keyword-content h2 { font-family: 'Outfit', sans-serif; font-weight: 800; font-size: 1.75rem; color: #111827; margin-top: 3.5rem; margin-bottom: 1rem; padding-bottom: 0.75rem; border-bottom: 2px solid #f3f4f6; }
        .keyword-content h3 { font-family: 'Outfit', sans-serif; font-weight: 700; font-size: 1.25rem; color: #1f2937; margin-top: 2rem; margin-bottom: 0.75rem; }
        .keyword-content p { margin-bottom: 1.25rem; color: #374151; }
        .keyword-content strong { color: #111827; font-weight: 700; }
        .keyword-content code { background: #f0f9ff; color: #0088CC; padding: 0.15rem 0.5rem; border-radius: 0.375rem; font-size: 0.9em; font-weight: 500; }
        .keyword-content a { color: #0088CC; text-decoration: none; }
        .keyword-content a:hover { text-decoration: underline; }
        .keyword-content ul, .keyword-content ol { margin-bottom: 1.5rem; padding-left: 1.5rem; }
        .keyword-content ul { list-style: none; }
        .keyword-content ul li { position: relative; padding-left: 1.5rem; margin-bottom: 0.5rem; color: #374151; }
        .keyword-content ul li::before { content: ''; position: absolute; left: 0; top: 0.6rem; width: 6px; height: 6px; background: #10b981; border-radius: 50%; }
        .keyword-content ol { list-style: none; counter-reset: step-counter; }
        .keyword-content ol li { position: relative; padding-left: 2.5rem; margin-bottom: 0.75rem; color: #374151; counter-increment: step-counter; }
        .keyword-content ol li::before { content: counter(step-counter); position: absolute; left: 0; top: 0; width: 1.75rem; height: 1.75rem; background: #f0fdf4; color: #10b981; font-weight: 700; font-size: 0.8rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 1.5px solid #d1fae5; }
        .keyword-content table { width: 100%; border-collapse: separate; border-spacing: 0; margin: 2rem 0; border-radius: 1rem; overflow: hidden; border: 1px solid #e5e7eb; font-size: 0.875rem; }
        .keyword-content thead { background: #f8fafc; }
        .keyword-content th { padding: 0.875rem 1rem; text-align: left; font-weight: 700; color: #1f2937; border-bottom: 1px solid #e5e7eb; }
        .keyword-content td { padding: 0.875rem 1rem; border-bottom: 1px solid #f3f4f6; color: #4b5563; }
        .keyword-content tbody tr:last-child td { border-bottom: none; }
        .keyword-content tbody tr:hover { background: #f9fafb; }
      `}</style>
    </div>
  );
}

function SEOMeta({ page }) {
  React.useEffect(() => {
    document.title = page.metaTitle;
    const updateMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    updateMeta('description', page.metaDescription);
    updateMeta('keywords', page.keywords.join(', '));
    updateMeta('og:title', page.metaTitle, 'property');
    updateMeta('og:description', page.metaDescription, 'property');
    updateMeta('og:url', `https://telegramemailbot.com/${page.slug}`, 'property');
    const link = document.querySelector('link[rel="canonical"]') || (() => { const l = document.createElement('link'); l.rel = 'canonical'; document.head.appendChild(l); return l; })();
    link.href = `https://telegramemailbot.com/${page.slug}`;
  }, [page]);
  return null;
}

function FAQSchema({ page }) {
  const schema = { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": page.faqs.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}
